import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import authFetch from '../utils/api';
import './Header.css';

const Header = () => {
  const { user, logout, isTech } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res = await authFetch('/api/notifications/unread-count');
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count);
      }
    } catch {}
  };

  const fetchNotifications = async () => {
    try {
      const res = await authFetch('/api/notifications');
      if (res.ok) setNotifications(await res.json());
    } catch {}
  };

  const handleBellClick = async () => {
    if (!showDropdown) await fetchNotifications();
    setShowDropdown(prev => !prev);
  };

  const markAllRead = async () => {
    await authFetch('/api/notifications/read-all', { method: 'PUT' });
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
  };

  const markRead = async (id) => {
    await authFetch(`/api/notifications/${id}/read`, { method: 'PUT' });
    setUnreadCount(prev => Math.max(0, prev - 1));
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
  };

  const getIcon = (type) => {
    if (type === 'new_appointment') return '📅';
    if (type === 'new_invoice') return '🧾';
    if (type === 'new_customer') return '👤';
    if (type === 'delete') return '🗑️';
    return '🔔';
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr + 'Z').getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const showBell = !isTech();

  return (
    <header className="header">
      <div className="header__left">
        <span className="header__greeting">
          Welcome back, <span className="header__username">{user?.username}</span>
          {user?.role && user.role !== 'admin' && (
            <span className="header__role-badge">{user.role}</span>
          )}
        </span>
      </div>

      <div className="header__right">
        {showBell && (
          <div className="header__bell-wrap" ref={dropdownRef}>
            <button className="header__bell-btn" onClick={handleBellClick} aria-label="Notifications">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {unreadCount > 0 && (
                <span className="header__bell-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </button>

            {showDropdown && (
              <div className="header__notif-dropdown">
                <div className="header__notif-header">
                  <span>Notifications</span>
                  {unreadCount > 0 && (
                    <button className="header__notif-mark-all" onClick={markAllRead}>
                      Mark all read
                    </button>
                  )}
                </div>
                <div className="header__notif-list">
                  {notifications.length === 0 ? (
                    <div className="header__notif-empty">No notifications yet</div>
                  ) : (
                    notifications.map(n => (
                      <div
                        key={n.id}
                        className={`header__notif-item ${!n.is_read ? 'header__notif-item--unread' : ''}`}
                        onClick={() => !n.is_read && markRead(n.id)}
                      >
                        <span className="header__notif-icon">{getIcon(n.type)}</span>
                        <div className="header__notif-content">
                          <p className="header__notif-message">{n.message}</p>
                          <span className="header__notif-time">{timeAgo(n.created_at)}</span>
                        </div>
                        {!n.is_read && <span className="header__notif-dot" />}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <button className="header__user-btn" onClick={logout}>
          <span className="header__user-avatar">
            {user?.username?.charAt(0).toUpperCase()}
          </span>
          <span className="header__logout-text">Logout</span>
        </button>
      </div>
    </header>
  );
};

export default Header;
